import { Component } from 'react';
import { connect } from 'react-redux';
import { PlantDto } from '@trucktrax/trucktrax-ts-common';
import { getPlantList } from '../../../services/plantsService';
import { fetchIncomingTicketsWithCurrentStatus } from '../../../services/ticketsService';
import ticketingSocket from '../../../services/sockets/ticketingSocket';
import { setIncomingTickets } from '../../../store/actions/ticketActions';
import { incomingPlantProductLinesSelector } from '../../../store/selectors/incomingMaterialsSelectors';
import { isFeatureFlagEnabled } from '../../../util/featureFlagUtil';
import { INCOMING_MATERIALS_FLAG } from '../../../constants/appConstants';
import { ReduxState } from '../../../store';
import { TicketWithCurrentStatusDto } from '../../../types';

export interface IncomingMaterialsSubscriptionProps {
  // eslint-disable-next-line react/no-unused-prop-types -- read via isSubscriptionEnabled(props)
  userPermission: ReduxState['userPermission'];
  currentRegion?: ReduxState['currentRegion'];
  /** Comma-joined user product lines for getPlantList's inProductLines; '' when none. */
  plantProductLines: string;
  setIncomingTickets: (tickets: TicketWithCurrentStatusDto[]) => void;
}

function isSubscriptionEnabled(props: IncomingMaterialsSubscriptionProps): boolean {
  const regionUrl = props.currentRegion?.url;
  if (!regionUrl) return false;
  return isFeatureFlagEnabled(props.userPermission, regionUrl, INCOMING_MATERIALS_FLAG);
}

/**
 * App-level owner of the Incoming Materials ticket subscription (DEV-4154).
 * Renders nothing — it exists to keep `incomingTicketsList` live from
 * anywhere in GeoTrax, because the arrival notification's whole job is
 * off-page: the nav dot must light up before the user ever visits the
 * Incoming Materials page and keep working after they leave it. That is why
 * this cannot live in the page-mounted IncomingMaterialsView/PanelContent
 * (whose old lifecycle seeded on mount and cleared the slice on unmount),
 * and why it is a component rather than a plain service: it needs React
 * lifecycle plus connected-prop reactivity (region changes, permissions
 * arriving after login).
 *
 * The single concern is "keep the slice live while the flag allows it";
 * everything here is an input to that:
 *  - turns the ticketing socket's IM reporting on while the ViewIncomingMaterials
 *    flag is enabled for the current region (off again when the flag drops,
 *    the region loses it, or this unmounts — e.g. leaving the GeoTrax app)
 *  - REST-seeds the slice on enable/region/product-line change and on tab refocus (background
 *    tabs can drop SignalR frames; the per-ticket merge in incomingTicketsReducer
 *    keeps a newer socket status from being clobbered by a staler response)
 *
 * IncomingMaterialsPanelContent and IncomingMaterialsView only read the slice.
 */
export class IncomingMaterialsSubscription extends Component<IncomingMaterialsSubscriptionProps> {
  // Guards async seed dispatches against unmount races: a late response must
  // not resurrect tickets after componentWillUnmount cleared them.
  mounted = false;

  // Monotonic seed sequence: only the most recently issued seed may dispatch.
  // Without it, two overlapping same-region seeds (e.g. enable() racing a
  // visibilitychange reseed) can resolve out of order, and the stale response
  // could resurrect a ticket the BE closed between the two requests.
  seedToken = 0;

  componentDidMount() {
    this.mounted = true;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    if (isSubscriptionEnabled(this.props)) {
      this.enable();
    }
  }

  componentDidUpdate(prevProps: IncomingMaterialsSubscriptionProps) {
    const wasEnabled = isSubscriptionEnabled(prevProps);
    const nowEnabled = isSubscriptionEnabled(this.props);

    if (!wasEnabled && nowEnabled) {
      this.enable();
      return;
    }
    if (wasEnabled && !nowEnabled) {
      this.disable();
      return;
    }
    const regionChanged = prevProps.currentRegion?.url !== this.props.currentRegion?.url;
    // The user's product lines can arrive after mount: App dispatches them from the token on its own mount,
    // which runs after this child's.
    const plantProductLinesChanged = prevProps.plantProductLines !== this.props.plantProductLines;
    if (nowEnabled && (regionChanged || plantProductLinesChanged)) {
      // Clear immediately so the old scope's tickets can't leak across while
      // the new seed fetch is in flight.
      this.props.setIncomingTickets([]);
      this.seed();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (isSubscriptionEnabled(this.props)) {
      this.disable();
    }
  }

  handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    if (!isSubscriptionEnabled(this.props)) return;
    this.seed();
  };

  enable = () => {
    ticketingSocket.setReportIncomingMaterialTickets(true);
    this.seed();
  };

  disable = () => {
    ticketingSocket.setReportIncomingMaterialTickets(false);
    this.props.setIncomingTickets([]);
  };

  seed = async () => {
    const regionUrl = this.props.currentRegion?.url;
    if (!regionUrl) return;
    const requestedRegion = regionUrl;
    this.seedToken += 1;
    const requestedToken = this.seedToken;

    const { plantProductLines } = this.props;
    if (!plantProductLines) {
      // No user product lines (or not loaded yet): no plants in scope, so no tickets. An
      // unfiltered getPlantList would return every product line's plants.
      this.props.setIncomingTickets([]);
      return;
    }

    let plants: PlantDto[];
    try {
      plants =
        (await getPlantList(regionUrl, undefined, undefined, undefined, plantProductLines)) ?? [];
    } catch {
      // Transient plants failure — keep whatever tickets we have rather than
      // conflating "couldn't learn the plants" with "no plants in scope"
      // (clearing would extinguish a live notification). The next socket
      // event or refocus reseed catches the slice up.
      return;
    }
    if (!this.isSeedStillCurrent(requestedRegion, requestedToken)) return;

    const destinationPlants = plants.map(p => p.url).filter((url): url is string => !!url);
    if (destinationPlants.length === 0) {
      // Region genuinely has no plants in scope — make sure no stale
      // tickets linger
      this.props.setIncomingTickets([]);
      return;
    }

    try {
      const incoming = await fetchIncomingTicketsWithCurrentStatus(destinationPlants);
      if (!this.isSeedStillCurrent(requestedRegion, requestedToken)) return;
      this.props.setIncomingTickets(incoming);
    } catch {
      // Silently ignore — tickets remain as they were; the next socket event
      // or refocus reseed will catch the slice up
    }
  };

  // A seed response only applies if we're still mounted, still enabled, still
  // on the region the request was issued for, and no newer seed has started.
  isSeedStillCurrent = (requestedRegion: string, requestedToken: number): boolean =>
    this.mounted &&
    this.seedToken === requestedToken &&
    isSubscriptionEnabled(this.props) &&
    this.props.currentRegion?.url === requestedRegion;

  render() {
    return null;
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    userPermission: state.userPermission || {},
    currentRegion: state.currentRegion,
    plantProductLines: incomingPlantProductLinesSelector(state),
  };
}

export default connect(mapStateToProps, {
  setIncomingTickets,
})(IncomingMaterialsSubscription);
